'use client';

import React, { useEffect, useRef, useState } from 'react';

interface EnamadSealProps {
    id: string;
    code: string;
    /** Small tile for tight spots such as the footer bottom bar */
    compact?: boolean;
}

const EnamadSeal: React.FC<EnamadSealProps> = ({ id, code, compact = false }) => {
    const [isVisible, setIsVisible] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!containerRef.current || isVisible) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            {
                // Trigger 200px before the user reaches the footer
                rootMargin: '200px',
                threshold: 0.01,
            },
        );

        observer.observe(containerRef.current);

        return () => observer.disconnect();
    }, [isVisible]);

    const openEnamad = () => {
        const url = `https://trustseal.enamad.ir/?id=${id}&Code=${code}`;
        window.open(url, '_blank', 'referrerpolicy=origin,width=700,height=700');
    };

    return (
        <div
            ref={containerRef}
            className={`flex shrink-0 items-center justify-center bg-white shadow-sm ${
                compact
                    ? 'size-14 overflow-hidden rounded-xl p-1 ring-1 ring-zinc-200/80 dark:ring-white/10'
                    : 'w-24 h-24 min-h-[96px] min-w-[96px] rounded-lg p-2'
            }`}
        >
            {isVisible ? (
                <a
                    role='button'
                    tabIndex={0}
                    onClick={openEnamad}
                    onKeyDown={(e) => e.key === 'Enter' && openEnamad()}
                    className='cursor-pointer inline-block'
                >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                        referrerPolicy='origin'
                        src={`https://trustseal.enamad.ir/logo.aspx?id=${id}&Code=${code}`}
                        alt='نماد اعتماد الکترونیکی'
                        className={`${compact ? 'w-12' : 'w-20'} h-auto object-contain`}
                        data-code={code}
                        loading='lazy'
                    />
                </a>
            ) : (
                // Lightweight placeholder to prevent layout shifts (CLS)
                <div
                    className={`${compact ? 'size-10' : 'size-16'} bg-gray-100 dark:bg-gray-200 rounded animate-pulse`}
                />
            )}
        </div>
    );
};

export default EnamadSeal;
